/* eslint-disable camelcase */
import React, { useState, useEffect, useContext } from 'react';
import { navigate } from 'gatsby';
import {
  Button,
  Dialog,
} from '@material-ui/core'

import SaveTwoToneIcon from '@material-ui/icons/SaveTwoTone';
import ReplayTwoToneIcon from '@material-ui/icons/ReplayTwoTone';
import DeleteTwoToneIcon from '@material-ui/icons/DeleteTwoTone';
import AirplanemodeActiveTwoToneIcon from '@material-ui/icons/AirplanemodeActiveTwoTone';

import { MyCardRow } from 'components/Elements/MyCard';
import MyButton from 'components/Elements/MyButton';
import MyAutoComplete from 'components/Elements/MyAutoComplete';
import MyInput from 'components/Elements/MyInput';
import MyDescription from 'components/Elements/MyDescription';
import MyDatePicker from 'components/Elements/MyDatePicker';

import { CircularProgressContext } from 'components/CircularProgressOverlay';
import { NotificationContext } from 'components/NotificationOverlay';

import { siteSetting } from 'services/loginService';
import { airFlightService } from 'services/airFlightService';
import { DialogTitleContainer, DialogContentContainer, DialogActionsContainer } from './style';

const moment = require('moment-timezone');

const FlightModal = ({
  flightEditOpen,
  updateFlightInfo,
  handleFlightEditClose,
  apiToken,
  timezone,
}) => {
  if (!flightEditOpen) {
    return null;
  }

  const [values, setValues] = useState(null);
  const [isValid, setValid] = useState(false);
  const [fieldErrors, setFieldErrors] = useState(null);
  const [flightDetail, setFlightDetail] = useState(null);
  const { showCircularProgress, hideCircularProgress } = useContext(CircularProgressContext);
  const { showNotification } = useContext(NotificationContext);

  const handleCheckFlight = async (info) => {
    let icao_code = null;
    let flight_number = null;
    let flight_date = null;
    if (info) {
      ({ icao_code, flight_number, flight_date } = info);
    } else {
      ({ icao_code, flight_number, flight_date } = values);
    }

    if (icao_code && flight_number && flight_date) {
      showCircularProgress('Checking...');
      const data = await airFlightService.get_flights({
        icao_code,
        flight_number,
        flight_date,
        token: apiToken
      });
      hideCircularProgress();
      if (data && data.data.status === 'SUCCESS') {
        setFlightDetail({ ...data.data.data });
        setValid(!info);
      } else {
        setFlightDetail(null);
        setValid(false);
      }
    }
  }

  useEffect(() => {
    if (flightEditOpen) {
      setFlightDetail(null);
      setValues({
        ...flightEditOpen,
      });
      setValid(false);

      if (flightEditOpen.icao_code && flightEditOpen.flight_number && flightEditOpen.flight_date) {
        handleCheckFlight({ ...flightEditOpen });
      }
    } else {
      setValues(null);
      setFlightDetail(null);
    }
  }, [flightEditOpen]);

  const handleDetailChange = (id, newValue) => {
    setValues({
      ...values,
      [id]: newValue,
    });
    setValid(false);
  }

  const handleSave = () => {
    if (!isValid) {
      return;
    }
    updateFlightInfo({
      ...values,
    });
  }

  const handleRemove = () => {
    updateFlightInfo({
      type: values.type
    });
  }

  const handleLoadAirlines = async ({ search }) => {
    if (search?.length >= 3) {
      const data = await airFlightService.get_airlines({ search, token: apiToken });
      if (data && data.data?.data?.data?.length > 0) {
        const formatted = data.data.data.data.map(item => ({ ...item, title: item.airline_name }));
        return formatted;
      }
    }
    return [];
  }

  const handleAirlineChange = airline => {
    setValues({
      ...values,
      airline_name: airline?.airline_name || null,
      icao_code: airline?.icao_code || null,
      iata_code: airline?.iata_code || null,
      airline,
    });
  }

  const timeConvert = t => t ? moment(t).tz(timezone || siteSetting().timezone).format('dddd, MMMM Do YYYY, h:mm A') : '';

  return (
    <Dialog
      classes={{ paper: 'modal-content' }}
      fullWidth
      maxWidth="sm"
      open={flightEditOpen}
      onClose={handleFlightEditClose}
      disableBackdropClick
      disableEscapeKeyDown
    >
      <DialogTitleContainer className="bg-secondary">
        <span>
          Flight Information
        </span>
      </DialogTitleContainer>
      <DialogContentContainer className="p-0" dividers>
        <div className="dialog-content--wrapper flight-modal-content bg-secondary border-0">
          <div className="card-body px-lg-4 py-lg-3">
            <div className="card-body--column w-100">
              <MyCardRow>
                <MyAutoComplete
                  label="Airline"
                  loadDataCallback={handleLoadAirlines}
                  onChange={handleAirlineChange}
                  defaultValue={values?.airline_name ? { ...values, title: values.airline_name } : null}
                />
                <MyInput
                  label="Flight Number"
                  id="flight_number"
                  type="number"
                  values={values}
                  onChange={handleDetailChange}
                />
                <MyDatePicker label="Flight Date" id="flight_date" values={values} onChange={handleDetailChange} />
              </MyCardRow>
              <MyCardRow>
                <div />
                <MyButton
                  type="info"
                  onClick={() => handleCheckFlight()}
                >
                  <span className="btn-wrapper--icon">
                    <AirplanemodeActiveTwoToneIcon fontSize="small" />
                  </span>
                  <span className="btn-wrapper--label">Check Flight</span>
                </MyButton>
                <div />
              </MyCardRow>
              <MyCardRow>
                <MyDescription>
                  <p style={{ display: 'flex', justifyContent: 'space-between' }}><b>Flight Date:</b> <span>{flightDetail?.scheduled_off ? timeConvert(flightDetail?.scheduled_off) : 'N/A'}</span></p>
                  <p style={{ display: 'flex', justifyContent: 'space-between' }}><b>Departure Airport:</b> <span>{flightDetail?.origin?.name || 'N/A'}</span></p>
                  <p style={{ display: 'flex', justifyContent: 'space-between' }}><b>Departure Estimated:</b> <span>{flightDetail?.estimated_off ? timeConvert(flightDetail?.estimated_off) : 'N/A'}</span></p>
                  <p style={{ display: 'flex', justifyContent: 'space-between' }}><b>Arrival Airport:</b> <span>{flightDetail?.destination?.name || 'N/A'}</span></p>
                  <p style={{ display: 'flex', justifyContent: 'space-between' }}><b>Arrival Terminal:</b> <span>{flightDetail?.terminal_destination || 'N/A'}</span></p>
                  <p style={{ display: 'flex', justifyContent: 'space-between' }}><b>Arrival Baggage:</b> <span>{flightDetail?.baggage_claim || 'N/A'}</span></p>
                  <p style={{ display: 'flex', justifyContent: 'space-between' }}><b>Arrival Delay:</b> <span>{flightDetail?.arrival_delay ? `${flightDetail?.arrival_delay || 0} min` : 'N/A'}</span></p>
                  <p style={{ display: 'flex', justifyContent: 'space-between' }}><b>Arrival Scheduled:</b> <span>{flightDetail?.scheduled_in ? timeConvert(flightDetail?.scheduled_in) : 'N/A'}</span></p>
                  <p style={{ display: 'flex', justifyContent: 'space-between' }}><b>Arrival Estimated:</b> <span>{flightDetail?.estimated_in ? timeConvert(flightDetail?.estimated_in) : 'N/A'}</span></p>
                </MyDescription>
              </MyCardRow>
            </div>
          </div>
        </div>
      </DialogContentContainer>
      <DialogActionsContainer className="bg-secondary p-3">
        <Button
          size="small"
          className="btn-primary mxr-4 px-4 py-2 btn-transition-none"
          onClick={handleSave}
          disabled={!isValid}
        >
          <span className="btn-wrapper--icon">
            <SaveTwoToneIcon fontSize="small" />
          </span>
          <span className="btn-wrapper--label">Save</span>
        </Button>
        <Button
          size="small"
          className="btn-danger mxr-4 px-4 py-2 btn-transition-none"
          onClick={handleRemove}
        >
          <span className="btn-wrapper--icon">
            <DeleteTwoToneIcon fontSize="small" />
          </span>
          <span className="btn-wrapper--label">Remove</span>
        </Button>
        <Button
          size="small"
          className="btn-warning px-4 py-2 btn-transition-none"
          onClick={handleFlightEditClose}
        >
          <span className="btn-wrapper--icon">
            <ReplayTwoToneIcon fontSize="small" />
          </span>
          <span className="btn-wrapper--label">Close</span>
        </Button>
      </DialogActionsContainer>
    </Dialog>
  );
};

export default FlightModal;
